import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Inbox } from 'lucide-react';
import { useAdminDashboardStats } from '@/hooks/useAdminDashboardStats';
import { useAdminDashboardTimeSeries } from '@/hooks/useAdminDashboardTimeSeries';
import { TenantStatusCards } from '@/components/dashboard/TenantStatusCards';
import { GlobalMessageStats } from '@/components/dashboard/GlobalMessageStats';
import { TopTenantsTable } from '@/components/dashboard/TopTenantsTable';
import { PlanDistributionChart } from '@/components/dashboard/PlanDistributionChart';
import { ProviderHealthCard } from '@/components/dashboard/ProviderHealthCard';
import { SystemAlertsCard } from '@/components/dashboard/SystemAlertsCard';
import { TenantGrowthChart } from '@/components/dashboard/TenantGrowthChart';
import { GlobalMessagesTimeChart } from '@/components/dashboard/GlobalMessagesTimeChart';
import { UserGrowthChart } from '@/components/dashboard/UserGrowthChart';
import { PlatformUsageCard } from '@/components/dashboard/PlatformUsageCard';

const AdminDashboard = () => {
  const [period, setPeriod] = useState('30');
  const { data: stats, isLoading, refetch } = useAdminDashboardStats();
  const { data: timeSeries, isLoading: isLoadingTimeSeries, refetch: refetchTimeSeries } = useAdminDashboardTimeSeries(parseInt(period));

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetch();
        refetchTimeSeries();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refetch, refetchTimeSeries]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard Admin</h1>
          <p className="text-muted-foreground">Visão geral do sistema UaizyChat</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 dias</SelectItem>
            <SelectItem value="14">Últimos 14 dias</SelectItem>
            <SelectItem value="30">Últimos 30 dias</SelectItem>
            <SelectItem value="90">Últimos 90 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tenant Status Cards */}
      <TenantStatusCards stats={stats?.tenants} isLoading={isLoading} />

      {/* Additional Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Usuários</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.totalUsers || 0}</div>
                <p className="text-xs text-muted-foreground">Usuários registrados na plataforma</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Inboxes</CardTitle>
            <Inbox className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.totalInboxes || 0}</div>
                <p className="text-xs text-muted-foreground">Caixas de entrada configuradas</p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Card de uso por plataforma PWA vs Browser */}
        <PlatformUsageCard />
      </div>

      {/* Global Message Stats */}
      <GlobalMessageStats stats={stats?.messages} isLoading={isLoading} />

      {/* Time Series Charts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <TenantGrowthChart 
          data={timeSeries?.tenantsByDay || []} 
          totalTenants={timeSeries?.totalTenants || 0}
          isLoading={isLoadingTimeSeries} 
        />
        <UserGrowthChart 
          data={timeSeries?.usersByDay || []} 
          totalUsers={timeSeries?.totalUsers || 0}
          isLoading={isLoadingTimeSeries} 
        />
      </div>

      {/* Messages Time Chart - Full Width */}
      <GlobalMessagesTimeChart 
        data={timeSeries?.messagesByDay || []} 
        totalMessages={timeSeries?.totalMessages || 0}
        isLoading={isLoadingTimeSeries} 
      />

      {/* Provider Health & System Alerts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ProviderHealthCard data={stats?.providerHealth || []} isLoading={isLoading} />
        <SystemAlertsCard data={stats?.systemAlerts || []} isLoading={isLoading} />
      </div>

      {/* Top Tenants & Plan Distribution */}
      <div className="grid gap-4 lg:grid-cols-2">
        <TopTenantsTable data={stats?.topTenants || []} isLoading={isLoading} />
        <PlanDistributionChart data={stats?.planDistribution || []} isLoading={isLoading} />
      </div>

      {/* Quick Access Card */}
      <Card>
        <CardHeader>
          <CardTitle>Bem-vindo ao UaizyChat Admin</CardTitle>
          <CardDescription>
            Gerencie clientes, usuários e configurações do sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Use o menu lateral para navegar entre as seções de administração.
            Como Root Admin, você tem acesso completo a todas as funcionalidades do sistema.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
